import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom'; // Import Link for navigation

const DataAnalysisPage = () => { // Renamed to DataAnalysisPage for consistency
    // Intersection Observer logic for fade-in animations
    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer;
            }
        });
        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleSectionIntersect]);

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Global Styles for animations (same as App.js for consistency) */}
            <style>
                {`
                .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                .fade-in-up-active { opacity: 1; transform: translateY(0); }
                `}
            </style>

            {/* Section 1: Hero/Introduction - With Image Background */}
            <section
                className="relative py-20 text-center px-6 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url('https://i.imgur.com/CM0ZfGk.png')` }}
            >
                {/* Overlay for background image brightness */}
                <div className="absolute inset-0 bg-black bg-opacity-20"></div> {/* Changed opacity to 20% */}
                <div className="relative z-10 container mx-auto flex flex-col items-center justify-center">
                    {/* Added a div for the rounded black overlay */}
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        <h1 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-6">
                            Data Analysis Services
                        </h1>
                        <p className="text-xl md:text-2xl text-[#F8F8F8] max-w-3xl mx-auto mb-10">
                            Unlock the power of your data with our comprehensive data analysis services. We transform raw data into actionable insights, helping you make informed business decisions and identify growth opportunities.
                        </p>
                        <Link to="/get-a-quote" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                            Get a Data Analysis Quote
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 2: Core Services - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12 fade-in-up" ref={setSectionRef}>
                        Our Expertise
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {[
                            { title: "Trend Analysis", delay: "0.1s", text: "Identify patterns, forecast future trends, and stay ahead of the curve.", iconText: "📈" },
                            { title: "Custom Reporting & Dashboards", delay: "0.2s", text: "Receive tailored reports and interactive dashboards for clear, concise understanding.", iconText: "📊" },
                            { title: "Actionable Insights", delay: "0.3s", text: "Translate complex data into strategic business moves and growth opportunities.", iconText: "💡" },
                            { title: "Data Cleaning & Preparation", delay: "0.4s", text: "Ensure your data is accurate, consistent, and ready for analysis.", iconText: "🧹" },
                            { title: "Data Security & Compliance", delay: "0.5s", text: "Protecting your sensitive data with robust security measures and compliance adherence.", iconText: "🔒" },
                            { title: "Predictive Analytics", delay: "0.6s", text: "Leverage advanced models to predict future outcomes and optimize strategies.", iconText: "🧠" },
                        ].map(({ title, delay, text, iconText }, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105"
                                ref={setSectionRef}
                                style={{ animationDelay: delay }}
                            >
                                <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center text-5xl">
                                    {iconText}
                                </div>
                                <h3 className="text-2xl font-bold text-[#0A1128] mb-2">{title}</h3>
                                <p className="text-gray-700 text-lg leading-relaxed">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section 3: Call to Action - Blue Background */}
            <section
                className="relative py-20 text-center px-6 bg-cover bg-center bg-no-repeat border-t border-[#C9B072]"
                style={{ backgroundImage: `url('https://i.imgur.com/z6YsyZA.png')` }}
            >
                {/* Overlay for better text readability */}
                <div className="absolute inset-0 bg-black opacity-60"></div>
                <div className="relative z-10 container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-8 fade-in-up" ref={setSectionRef}>
                        Ready to Make Data-Driven Decisions?
                    </h2>
                    <p className="text-xl text-[#CCD2E3] mb-10 fade-in-up" ref={setSectionRef}>
                        Let's unlock the full potential of your business data together.
                    </p>
                    <Link to="/contact-us" className="px-12 py-4 bg-[#4CAF50] text-[#F8F8F8] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}>
                        Consult Our Data Experts
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default DataAnalysisPage;
